"use client";
import { Box, Typography, Stack } from "@mui/material";
import { Restaurant } from "@mui/icons-material";
import React, { useMemo } from "react";
import { groupNutritionTypes } from "./year-in-review-constants";

interface NutritionTargetsSlideProps {
  data: {
    total: number;
    types: Array<{
      type: string;
      count: number;
    }>;
  };
}

export default function NutritionTargetsSlide({
  data,
}: NutritionTargetsSlideProps) {
  const groupedTypes = useMemo(
    () => groupNutritionTypes(data.types),
    [data.types],
  );
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "#0a0a0a",
        position: "relative",
        color: "white",
        p: 4,
        textAlign: "center",
        "&::before": {
          content: '""',
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "100%",
          height: "100%",
          background:
            "radial-gradient(circle, rgba(245, 158, 11, 0.2) 0%, transparent 70%)",
          pointerEvents: "none",
        },
      }}
    >
      <Box sx={{ position: "relative", zIndex: 1, width: "100%" }}>
        <Restaurant
          sx={{
            fontSize: 60,
            mb: 3,
            color: "#f59e0b",
            animation: "wiggle 1s ease-in-out infinite",
            "@keyframes wiggle": {
              "0%, 100%": {
                transform: "rotate(-5deg)",
              },
              "50%": {
                transform: "rotate(5deg)",
              },
            },
          }}
        />
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            mb: 2,
            animation: "fadeIn 0.5s ease-out",
            "@keyframes fadeIn": {
              "0%": {
                opacity: 0,
              },
              "100%": {
                opacity: 1,
              },
            },
          }}
        >
          Nutrition Targets Created
        </Typography>
        <Typography
          variant="h1"
          sx={{
            fontWeight: 900,
            mb: 4,
            color: "#f59e0b",
            animation:
              "scaleIn 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55) 0.3s both",
            "@keyframes scaleIn": {
              "0%": {
                transform: "scale(0)",
                opacity: 0,
              },
              "100%": {
                transform: "scale(1)",
                opacity: 1,
              },
            },
          }}
        >
          {data.total}
        </Typography>
        <Stack
          spacing={2}
          sx={{ width: "100%", maxWidth: 400, margin: "0 auto" }}
        >
          {groupedTypes.map((item, index) => (
            <Box
              key={item.type}
              sx={{
                bgcolor: "rgba(245, 158, 11, 0.1)",
                border: "2px solid rgba(245, 158, 11, 0.3)",
                borderRadius: 3,
                p: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                animation: `slideUp 0.4s ease-out ${0.5 + index * 0.1}s both`,
                "@keyframes slideUp": {
                  "0%": {
                    transform: "translateY(20px)",
                    opacity: 0,
                  },
                  "100%": {
                    transform: "translateY(0)",
                    opacity: 1,
                  },
                },
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {item.type}
              </Typography>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: "#f59e0b" }}
              >
                {item.count}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    </Box>
  );
}
